import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  DispatchTrip,
  DispatchTripGridItem,
  DispatchTripFilter,
  DispatchTripNote,
  DispatcherUser,
  DriverTripCount,
  DispatchTripStatus,
} from "@app/shared/models/DispatchTrip.model";

@Injectable({
  providedIn: "root",
})
export class DispatchTripService {
  private baseUrl = "/api/DispatchTrip";

  constructor(private http: HttpClient) {}

  getAll(filter?: DispatchTripFilter): Observable<DispatchTripGridItem[]> {
    let params = new HttpParams();

    if (filter) {
      if (filter.dispatcherId) params = params.set("dispatcherId", filter.dispatcherId);
      if (filter.billToId) params = params.set("billToId", filter.billToId);
      if (filter.driverId) params = params.set("driverId", filter.driverId);
      if (filter.status !== undefined && filter.status !== null)
        params = params.set("status", filter.status.toString());
      if (filter.startDateFrom)
        params = params.set("startDateFrom", filter.startDateFrom.toISOString());
      if (filter.startDateTo) params = params.set("startDateTo", filter.startDateTo.toISOString());
    }

    return this.http.get<DispatchTripGridItem[]>(`${this.baseUrl}/list`, { params });
  }

  getById(id: string): Observable<DispatchTrip> {
    return this.http.get<DispatchTrip>(`${this.baseUrl}/find/${id}`);
  }

  create(trip: Partial<DispatchTrip>): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.baseUrl}/create`, trip);
  }

  update(trip: Partial<DispatchTrip>): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/update`, trip);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete/${id}`);
  }

  saveFiles(
    dispatchTripId: string,
    remainingFileIds: string[],
    newFiles: { fileName: string; originalFileName: string; contentType: string }[]
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/saveFiles`, {
      dispatchTripId,
      remainingFileIds,
      newFiles,
    });
  }

  updateDriver(tripId: string, driverId: string | null): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/updateDriver`, { tripId, driverId });
  }

  sendBatchEmails(tripIds: string[]): Observable<{ sentCount: number; failCount: number; total: number }> {
    return this.http.post<{ sentCount: number; failCount: number; total: number }>(
      `${this.baseUrl}/sendEmails`,
      { tripIds }
    );
  }

  addNote(
    dispatchTripId: string,
    note: string,
    status?: string,
    type?: string
  ): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.baseUrl}/addNote`, {
      dispatchTripId,
      note,
      status,
      type,
    });
  }

  deleteNote(noteId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/deleteNote/${noteId}`);
  }

  getCountByDriverAndDate(driverId: string, date: Date): Observable<number> {
    const params = new HttpParams()
      .set("driverId", driverId)
      .set("date", date.toISOString());
    return this.http.get<number>(`${this.baseUrl}/countByDriverAndDate`, { params });
  }

  getByDriverAndDateRange(
    driverIds: string[],
    startDate: Date,
    endDate: Date
  ): Observable<DriverTripCount[]> {
    let params = new HttpParams()
      .set("startDate", startDate.toISOString())
      .set("endDate", endDate.toISOString());

    driverIds.forEach((id) => {
      params = params.append("driverIds", id);
    });

    return this.http.get<DriverTripCount[]>(`${this.baseUrl}/byDriverAndDateRange`, { params });
  }

  getDispatchers(): Observable<DispatcherUser[]> {
    return this.http.get<DispatcherUser[]>(`${this.baseUrl}/dispatchers`);
  }

  sendClientEmail(tripId: string, overrides?: { to?: string; subject?: string; body?: string }): Observable<{ sentUtc: Date }> {
    return this.http.post<{ sentUtc: Date }>(`${this.baseUrl}/sendClientEmail`, {
      tripId,
      toOverride: overrides?.to,
      subjectOverride: overrides?.subject,
      bodyOverride: overrides?.body,
    });
  }

  sendDriverEmail(tripId: string, overrides?: { to?: string; subject?: string; body?: string }): Observable<{ sentUtc: Date }> {
    return this.http.post<{ sentUtc: Date }>(`${this.baseUrl}/sendDriverEmail`, {
      tripId,
      toOverride: overrides?.to,
      subjectOverride: overrides?.subject,
      bodyOverride: overrides?.body,
    });
  }

  getEmailPreview(tripId: string, type: 'client' | 'driver'): Observable<{ to: string; cc?: string; subject: string; body: string }> {
    return this.http.get<{ to: string; cc?: string; subject: string; body: string }>(`${this.baseUrl}/emailPreview`, {
      params: { tripId, type, _: Date.now().toString() },
    });
  }
}
